<template>
  <v-dialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    max-width="600px"
    :fullscreen="$vuetify.display.xs"
  >
    <v-card>
      <v-card-title>
        <div class="d-flex align-center justify-space-between">
          <typography weight="600" size="1.25rem">Customize Columns</typography>
          <v-btn icon color="normal" variant="text" @click="$emit('update:modelValue', false)">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card-title>
      <v-divider class="m-0"></v-divider>
      <v-card-item>
        <div class="d-flex flex-column gap-3">
          <!-- Search Input -->
          <v-text-field
            v-model="searchQuery"
            label="Search columns"
            placeholder="Type to filter columns..."
            prepend-inner-icon="mdi-magnify"
            density="compact"
            clearable
            variant="outlined"
            color="primary"
          ></v-text-field>

          <!-- Column Count -->
          <div class="text-caption text-medium-emphasis">
            {{ selectedCount }} of {{ filteredColumns.length }} columns selected
          </div>

          <!-- Select All / Deselect All Buttons -->
          <div class="d-flex gap-2">
            <v-btn
              size="small"
              variant="outlined"
              color="primary"
              @click="selectAll"
              :disabled="allSelected"
            >
              Select All
            </v-btn>
            <v-btn
              size="small"
              variant="outlined"
              color="primary"
              @click="deselectAll"
              :disabled="noneSelected"
            >
              Deselect All
            </v-btn>
          </div>

          <!-- Column List -->
          <div class="column-list" style="max-height: 400px; overflow-y: auto">
            <v-list density="compact">
              <v-list-item
                v-for="col in filteredColumns"
                :key="col.key"
                class="px-0"
              >
                <template v-slot:prepend>
                  <v-checkbox
                    :model-value="localColumns.find(c => c.key === col.key)?.display ?? col.display ?? true"
                    @update:model-value="toggleColumn(col.key, $event)"
                    color="primary"
                    density="compact"
                    hide-details
                    :aria-label="`Toggle ${col.title} column`"
                  ></v-checkbox>
                </template>
                <v-list-item-title>{{ col.title }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </div>
        </div>
      </v-card-item>
      <v-divider class="m-0"></v-divider>
      <v-card-actions class="d-flex justify-space-between pa-4">
        <v-btn
          variant="outlined"
          color="primary"
          @click="handleCancel"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          @click="handleApply"
        >
          Apply
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import type { tableColumn } from "@/hooks/useTableSort";
import Typography from "@/components/typography.vue";

const props = defineProps<{
  modelValue: boolean;
  columns: tableColumn[];
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "update:columns", columns: tableColumn[]): void;
}>();

const searchQuery = ref("");
const localColumns = ref<tableColumn[]>([]);

// Initialize local columns when modal opens or columns change
watch(
  [() => props.modelValue, () => props.columns],
  ([isOpen, cols]) => {
    if (isOpen && cols) {
      // Deep copy columns to avoid mutating props
      localColumns.value = cols.map((col) => ({
        ...col,
        display: col.display ?? true, // Default to true if not specified
      }));
    }
  },
  { immediate: true }
);

// Filter columns based on search query
const filteredColumns = computed(() => {
  if (!searchQuery.value) {
    return props.columns;
  }
  const query = searchQuery.value.toLowerCase();
  return props.columns.filter((col) =>
    col.title.toLowerCase().includes(query)
  );
});

// Count selected columns
const selectedCount = computed(() => {
  return localColumns.value.filter(
    (col) => col.display !== false
  ).length;
});

// Check if all filtered columns are selected
const allSelected = computed(() => {
  return filteredColumns.value.every((col) => {
    const localCol = localColumns.value.find((c) => c.key === col.key);
    return (localCol?.display ?? true) !== false;
  });
});

// Check if no filtered columns are selected
const noneSelected = computed(() => {
  return filteredColumns.value.every((col) => {
    const localCol = localColumns.value.find((c) => c.key === col.key);
    return (localCol?.display ?? true) === false;
  });
});

// Toggle a single column
const toggleColumn = (key: string, value: boolean) => {
  const col = localColumns.value.find((c) => c.key === key);
  if (col) {
    col.display = value;
  } else {
    // If column not in localColumns, add it
    const originalCol = props.columns.find((c) => c.key === key);
    if (originalCol) {
      localColumns.value.push({ ...originalCol, display: value });
    }
  }
};

// Select all filtered columns
const selectAll = () => {
  filteredColumns.value.forEach((col) => {
    toggleColumn(col.key, true);
  });
};

// Deselect all filtered columns
const deselectAll = () => {
  filteredColumns.value.forEach((col) => {
    toggleColumn(col.key, false);
  });
};

// Handle cancel - reset to original state
const handleCancel = () => {
  if (props.columns) {
    localColumns.value = props.columns.map((col) => ({
      ...col,
      display: col.display ?? true,
    }));
  }
  emit("update:modelValue", false);
};

// Handle apply - emit updated columns
const handleApply = () => {
  // Ensure all columns from props are included in the result
  const updatedColumns = props.columns.map((col) => {
    const localCol = localColumns.value.find((c) => c.key === col.key);
    return {
      ...col,
      display: localCol?.display ?? col.display ?? true,
    };
  });
  emit("update:columns", updatedColumns);
  emit("update:modelValue", false);
};
</script>

<style scoped lang="scss">
.column-list {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}
</style>

